import { useState } from 'react';
import { useRefinementList } from 'react-instantsearch';
import AccordionItem from '@monofront/design-system/components/Accordion';
import Label from '@monofront/design-system/components/Form/Label';
import { useTranslations } from 'next-intl';
import { RefinementListConnectorParams } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { ALGOLIA_INSIGHTS_EVENT_NAME } from '@monofront/business/constants';

type RefinementListProps = {
  attribute: RefinementListConnectorParams['attribute'];
  title: string;
  isToggled?: boolean;
  limit?: number;
  showMoreLimit?: RefinementListConnectorParams['showMoreLimit'];
  showMore?: RefinementListConnectorParams['showMore'];
  withMultipleScrolls?: boolean;
};

const Filter: React.FC<RefinementListProps> = ({
  attribute,
  limit,
  showMoreLimit,
  showMore,
  title,
  withMultipleScrolls,
  isToggled = false,
}) => {
  const { items, refine, sendEvent } = useRefinementList({
    attribute,
    showMore,
    showMoreLimit,
  });

  const [isExtended, setIsExtended] = useState(showMore ? false : true);

  const tGlobal = useTranslations('Global');

  if (items.length === 0) {
    return null;
  }

  const formattedFilters = items.slice(0, !isExtended ? limit : showMoreLimit);

  return (
    <AccordionItem
      title={title}
      isToggle={isToggled}
      withMultipleScrolls={withMultipleScrolls}
    >
      <ul>
        {formattedFilters.map((item, index) => {
          const idName = `${item.label}-${index}`;

          return (
            <li key={item.label}>
              <span
                onClick={(event) => {
                  event.preventDefault();
                  refine(item.value);

                  if (item.value.length > 0) {
                    // Not in the doc™️, but `click` forwards a `clickedFilters` event to insights
                    // See instantsearch.js/src/lib/utils/createSendEventForFacet.ts
                    sendEvent(
                      'click',
                      item.value,
                      ALGOLIA_INSIGHTS_EVENT_NAME.FILTER_CLICKED,
                    );
                  }
                }}
              >
                <div
                  className="checkbox"
                  role="checkbox"
                  aria-checked={item.isRefined}
                  aria-labelledby={idName}
                ></div>
                <Label id={idName}>{item.label}</Label>
              </span>
            </li>
          );
        })}
        {showMore && limit && items.length > limit && (
          <span
            className="tw-lf-link-md"
            onClick={() => setIsExtended(!isExtended)}
          >
            {!isExtended ? tGlobal('seeMore') : tGlobal('seeLess')}
          </span>
        )}
      </ul>
      <style jsx>{`
        ul {
          padding: var(--space-4) var(--space-4);
          max-height: 300px;
          overflow-y: ${isExtended ? `scroll` : `unset`};
          border-bottom: var(--border);
          background-color: var(--clr-primary-6);
        }

        ul:empty {
          display: none;
        }

        ul > li:not(:last-child) {
          margin-bottom: var(--space-2);
        }

        span {
          display: flex;
          cursor: pointer;
        }

        span > :global(label) {
          cursor: pointer;
        }

        span > :global(input) {
          margin-right: var(--space-4);
        }

        .checkbox {
          border: 2px solid var(--clr-checkbox-border);
          border-radius: 3px;
          width: 20px;
          min-width: 20px;
          height: 20px;
          background-color: var(--white);
          color: var(--white);
          margin-right: var(--space-4);
        }

        .checkbox:after {
          content: ' ';
          width: 20px;
          height: 20px;
          display: inline-block;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .checkbox[aria-checked='true']:after {
          transform: scale(0.7);
          background-color: var(--clr-primary-1);
          display: flex;
          width: 100%;
          height: 100%;
          border-radius: 2px;
        }

        @media (max-width: 800px) {
          ul {
            background-color: var(--clr-primary-5);
          }
        }
      `}</style>
    </AccordionItem>
  );
};

export default Filter;
